// Client-side GA4 helpers. Measurement IDs come from admin Settings
// (KV / public site-config) — never from Vite env as the source of truth.

export const GA_MEASUREMENT_RE = /^G-[A-Z0-9]+$/i;

export type Ga4Settings = {
  ga_measurement_id: string;
  ga_stream_id: string;
  ga_property_id: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function isValidGaMeasurementId(id: string | null | undefined): boolean {
  return GA_MEASUREMENT_RE.test((id || '').trim());
}

/** Property ID is digits only. Values that look like Measurement IDs are ignored. */
export function sanitizeGaPropertyIdInput(raw: string): string {
  const t = raw.trim();
  if (/^(G-|UA-|GT-)/i.test(t)) return '';
  return t.replace(/\D/g, '').slice(0, 15);
}

export function isGa4PublicPath(pathname: string, isLoggedIn: boolean): boolean {
  const p = pathname.toLowerCase();
  if (p.startsWith('/admin') || p.startsWith('/scan') || p.startsWith('/checkin')) return false;
  if (p.startsWith('/tv') || p.startsWith('/focus') || p.startsWith('/auth')) return false;
  if (p === '/home' || p.startsWith('/p/')) return true;
  if (p === '/board' || p.startsWith('/board/')) return true;
  if (p === '/login') return true;
  // Signed-out visitors see the marketing homepage at `/`; signed-in users
  // get the dashboard there and must never be tagged.
  if (p === '/') return !isLoggedIn;
  return false;
}

export function gaEvent(name: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

export function ensureGtag(measurementId: string) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
}

export function injectGtagScript(measurementId: string) {
  if (typeof document === 'undefined') return;
  const src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
}
